/**
 * NewDmModal Component
 *
 * Modal to start a new 1-on-1 direct message. Loads the list of active
 * profiles (excluding the current user) and finds-or-creates a DM
 * conversation when one is selected.
 *
 * @module components/messages/NewDmModal
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../services/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { getOrCreateDM as findOrCreateDM } from '../../services/chatService';
import { formatUserRole } from '../../utils/formatters';
import { toast } from '../../utils/toast';
import TaskAssigneeAvatar from '../tasks/shared/TaskAssigneeAvatar';

/**
 * Fetch chat-eligible users by querying the profiles table directly.
 * RLS allows authenticated users to read profile basics.
 */
const fetchChatUsers = async () => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, avatar_url, role')
    .order('full_name', { ascending: true });
  if (error) throw error;
  return data || [];
};

const NewDmModal = ({ show, onHide, onConversationOpen }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ['chat-users'],
    queryFn: fetchChatUsers,
    enabled: show,
    staleTime: 1000 * 60,
  });

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users
      .filter((u) => u.id !== user?.id)
      .filter((u) => {
        if (!term) return true;
        const name = (u.full_name || '').toLowerCase();
        const role = (u.role || '').toLowerCase();
        return name.includes(term) || role.includes(term);
      });
  }, [users, search, user]);

  const dmMutation = useMutation({
    mutationFn: async (otherUserId) => {
      const { data, error: dmError } = await findOrCreateDM(otherUserId);
      if (dmError) throw dmError;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Conversacion abierta');
      if (onConversationOpen && data?.id) onConversationOpen(data.id);
      handleClose();
    },
    onError: (err) => {
      toast.error(err?.message || 'No se pudo abrir la conversacion');
    },
  });

  const handleClose = () => {
    setSearch('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-chat-left-text me-2"></i>
          Nuevo mensaje directo
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Control
            type="search"
            placeholder="Buscar usuario por nombre o rol..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </Form.Group>

        {error && (
          <Alert variant="danger" className="mb-3">
            No se pudieron cargar los usuarios.
          </Alert>
        )}

        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <div className="messages-user-list">
            {filteredUsers.length === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="bi bi-people" style={{ fontSize: '1.75rem' }}></i>
                <div className="mt-2 small">No se encontraron usuarios.</div>
              </div>
            ) : (
              filteredUsers.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  className="messages-user-row"
                  onClick={() => dmMutation.mutate(u.id)}
                  disabled={dmMutation.isLoading}
                >
                  <TaskAssigneeAvatar assignee={u} size="md" />
                  <div className="messages-user-info">
                    <div className="messages-user-name">
                      {u.full_name || 'Usuario'}
                    </div>
                    <div className="messages-user-role">
                      {formatUserRole(u.role)}
                    </div>
                  </div>
                  {dmMutation.isLoading && dmMutation.variables === u.id && (
                    <Spinner animation="border" size="sm" />
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

NewDmModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConversationOpen: PropTypes.func,
};

export default NewDmModal;
